import {
  Button,
  Checkbox,
  Drawer,
  FormControlLabel,
  FormGroup,
  Stack,
  Typography,
} from "@mui/material"
import { useFeatureFlagEnabled } from "posthog-js/react"
import { useContext, useEffect, useRef } from "react"
import { useTranslation } from "react-i18next"
import {
  useClearRefinements,
  useRefinementList,
  useToggleRefinement,
} from "react-instantsearch-core"

import useResponsive from "@sap/hooks/useResponsive"

import { ItemsContext } from "@/pages/items/context"
import { DomainContext } from "@/routes/providers/originals"
import PriceRangeFilter from "@/section/items/PriceRangeFilter"
import { sendFilterChangedEvent, sendFilterPanelToggleEvent } from "@/utils/posthog"

import LcaFirstLifeRatingFilter from "./LcaFirstLifeRatingFilter"
import MaterialFilter from "./MaterialFilter"
import UpholsteryFilter from "./UpholsteryFilter"
import { FilterCheckboxCheckedIcon, FilterCheckboxIcon } from "./components/FilterCheckboxIcons"
import FilterSection, { CountBadge } from "./components/FilterSection"

const PRICE_SEARCH_FEATURE_FLAG = "price_sort_filter"
const MATERIAL_FILTER_FEATURE_FLAG = "material_filter"
const UPHOLSTERY_FILTER_FEATURE_FLAG = "upholstery_filter"

export default function Filters() {
  const lcaFilterEnabled = useFeatureFlagEnabled("lca_filter")
  const materialFilterEnabled = useFeatureFlagEnabled(MATERIAL_FILTER_FEATURE_FLAG)
  const priceSearchEnabled = useFeatureFlagEnabled(PRICE_SEARCH_FEATURE_FLAG)
  const upholsteryFilterEnabled = useFeatureFlagEnabled(UPHOLSTERY_FILTER_FEATURE_FLAG)

  return (
    <>
      <FilterDrawer
        showLcaFilter={!!lcaFilterEnabled}
        showMaterialFilter={!!materialFilterEnabled}
        showPriceFilter={!!priceSearchEnabled}
        showUpholsteryFilter={!!upholsteryFilterEnabled}
      />
      <FilterSideBar
        showLcaFilter={!!lcaFilterEnabled}
        showMaterialFilter={!!materialFilterEnabled}
        showPriceFilter={!!priceSearchEnabled}
        showUpholsteryFilter={!!upholsteryFilterEnabled}
      />
    </>
  )
}

function FilterDrawer({
  showLcaFilter,
  showMaterialFilter,
  showPriceFilter,
  showUpholsteryFilter,
}: {
  showLcaFilter: boolean
  showMaterialFilter: boolean
  showPriceFilter: boolean
  showUpholsteryFilter: boolean
}) {
  const downMd = useResponsive("down", "md")
  const { t } = useTranslation("items")
  const { showFilters, setShowFilters } = useContext(ItemsContext)
  const { refine: clearFilters, canRefine } = useClearRefinements()
  const isOpen = showFilters && downMd
  const previousOpenRef = useRef<boolean | null>(null)

  useEffect(() => {
    if (previousOpenRef.current === null) {
      previousOpenRef.current = isOpen
      return
    }

    if (previousOpenRef.current !== isOpen) {
      sendFilterPanelToggleEvent({
        panel: "mobile_drawer",
        isOpen,
      })
      previousOpenRef.current = isOpen
    }
  }, [isOpen])

  return (
    <Drawer
      anchor={"bottom"}
      open={isOpen}
      onClose={() => {
        setShowFilters(false)
      }}
      slotProps={{
        paper: {
          sx: {
            borderTopLeftRadius: 16,
            borderTopRightRadius: 16,
            maxHeight: "calc(100% - 64px)",
            height: "calc(100% - 64px)",
            display: "flex",
          },
        },
      }}
    >
      <Stack
        sx={{
          minHeight: 0,
          flexGrow: 1,
        }}
      >
        <FiltersPanel
          showLcaFilter={showLcaFilter}
          showMaterialFilter={showMaterialFilter}
          showPriceFilter={showPriceFilter}
          showUpholsteryFilter={showUpholsteryFilter}
          sx={{
            paddingX: 4,
            paddingTop: 3,
            paddingBottom: 3,
            overflowY: "auto",
            minHeight: 0,
          }}
        />
      </Stack>
      <Stack
        direction="row"
        spacing={2}
        sx={{
          padding: 2,
          position: "sticky",
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 2,
          backgroundColor: "background.paper",
          borderTop: "1px solid",
          borderColor: "divider",
          transform: "translate3d(0,0,0)",
        }}
      >
        <Button
          variant="soft"
          fullWidth
          color="inherit"
          size="large"
          disabled={!canRefine}
          onClick={() => {
            sendFilterChangedEvent({
              filterGroup: "all",
              filterKey: "reset_all",
              isActive: false,
            })
            clearFilters()
            setShowFilters(false)
          }}
        >
          {t("ResetFilters")}
        </Button>
        <Button
          variant="soft"
          fullWidth
          color="primary"
          size="large"
          onClick={() => {
            setShowFilters(false)
          }}
        >
          {t("ShowResults")}
        </Button>
      </Stack>
    </Drawer>
  )
}

function FilterSideBar({
  showLcaFilter,
  showMaterialFilter,
  showPriceFilter,
  showUpholsteryFilter,
}: {
  showLcaFilter: boolean
  showMaterialFilter: boolean
  showPriceFilter: boolean
  showUpholsteryFilter: boolean
}) {
  return (
    <FiltersPanel
      showLcaFilter={showLcaFilter}
      showMaterialFilter={showMaterialFilter}
      showPriceFilter={showPriceFilter}
      showUpholsteryFilter={showUpholsteryFilter}
      sx={{
        paddingRight: 4,
        width: "280px",
        flexShrink: 0,
        alignSelf: "flex-start",
        display: {
          md: "block",
          xs: "none",
        },
      }}
    />
  )
}

function FiltersPanel({
  showLcaFilter,
  showMaterialFilter,
  showPriceFilter,
  showUpholsteryFilter,
  sx,
}: {
  showLcaFilter: boolean
  showMaterialFilter: boolean
  showPriceFilter: boolean
  showUpholsteryFilter: boolean
  sx?: Record<string, unknown>
}) {
  return (
    <Stack sx={sx}>
      <Categories />
      <Properties />
      {showUpholsteryFilter && <UpholsteryFilter />}
      {showMaterialFilter && <MaterialFilter />}
      {showPriceFilter && <PriceRangeFilter />}
      {showLcaFilter && <LcaFirstLifeRatingFilter />}
    </Stack>
  )
}

function Categories() {
  const { t } = useTranslation("items")
  const {
    items,
    refine: refineCategory,
    canRefine: hasCategory,
  } = useRefinementList({
    attribute: "category_plural",
    sortBy: ["name:asc"],
    limit: 20,
  })

  const activeCount = items.filter((item) => item.isRefined).length

  if (!hasCategory) {
    return null
  }

  return (
    <FilterSection
      title={t("category")}
      activeCount={activeCount}
      defaultExpanded
      trackingKey="category"
    >
      <Stack>
        {items.map((category) => (
          <FormGroup key={category.label}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={category.isRefined}
                  onChange={() => {
                    sendFilterChangedEvent({
                      filterGroup: "category",
                      filterKey: String(category.value),
                      filterLabel: category.label,
                      isActive: !category.isRefined,
                      value: category.label,
                      metadata: {
                        result_count: category.count,
                      },
                    })
                    refineCategory(category.value)
                  }}
                  size="small"
                  icon={<FilterCheckboxIcon />}
                  checkedIcon={<FilterCheckboxCheckedIcon />}
                />
              }
              label={
                <Stack
                  direction="row"
                  spacing={1}
                  sx={{
                    alignItems: "center",
                    justifyContent: "space-between",
                    width: "100%",
                  }}
                >
                  <Typography
                    sx={{
                      flexGrow: 1,
                      fontSize: "0.875rem",
                    }}
                  >
                    {category.label}
                  </Typography>
                  <CountBadge value={category.count} />
                </Stack>
              }
              sx={{
                marginRight: 0,
                marginLeft: "-7px",
                "& .MuiFormControlLabel-label": {
                  width: "100%",
                },
              }}
            />
          </FormGroup>
        ))}
      </Stack>
    </FilterSection>
  )
}

function Properties() {
  const { t } = useTranslation("items")
  const { isOriginals } = useContext(DomainContext)

  const { value: brandNew, refine: refineBrandNew } = useToggleRefinement({
    attribute: "brand_new",
  })

  const { value: outdoor, refine: refineOutdoor } = useToggleRefinement({
    attribute: "outdoor",
  })

  const { value: indoor, refine: refineIndoor } = useToggleRefinement({
    attribute: "indoor",
  })

  const { value: stackable, refine: refineStackable } = useToggleRefinement({
    attribute: "stackable",
  })

  const { value: sustainable, refine: refineSustainable } = useToggleRefinement({
    attribute: "sustainable",
  })

  const { value: satellietOriginals, refine: refineSatellietOriginals } = useToggleRefinement({
    attribute: "satelliet_originals",
  })

  const propertyFilters = [
    {
      key: "brand_new",
      checked: brandNew.isRefined,
      count: brandNew.onFacetValue.count || 0,
      onChange: (checked: boolean) => refineBrandNew({ isRefined: !checked }),
    },
    {
      key: "indoor",
      checked: indoor.isRefined,
      count: indoor.onFacetValue.count || 0,
      onChange: (checked: boolean) => refineIndoor({ isRefined: !checked }),
    },
    {
      key: "outdoor",
      checked: outdoor.isRefined,
      count: outdoor.onFacetValue.count || 0,
      onChange: (checked: boolean) => refineOutdoor({ isRefined: !checked }),
    },
    {
      key: "stackable",
      checked: stackable.isRefined,
      count: stackable.onFacetValue.count || 0,
      onChange: (checked: boolean) => refineStackable({ isRefined: !checked }),
    },
    {
      key: "sustainable",
      checked: sustainable.isRefined,
      count: sustainable.onFacetValue.count || 0,
      onChange: (checked: boolean) => refineSustainable({ isRefined: !checked }),
    },
    ...(!isOriginals
      ? [
          {
            key: "satelliet_originals",
            checked: satellietOriginals.isRefined,
            count: satellietOriginals.onFacetValue.count || 0,
            onChange: (checked: boolean) => refineSatellietOriginals({ isRefined: !checked }),
          },
        ]
      : []),
  ]

  const activeCount = propertyFilters.filter((property) => property.checked).length

  return (
    <FilterSection title={t("Properties")} activeCount={activeCount} trackingKey="properties">
      <Stack
        sx={{
          gap: 0.5,
        }}
      >
        {propertyFilters.map((property) => (
          <FormControlLabel
            key={property.key}
            control={
              <Checkbox
                checked={property.checked}
                onChange={(event) => {
                  sendFilterChangedEvent({
                    filterGroup: "properties",
                    filterKey: property.key,
                    filterLabel: t(property.key),
                    isActive: !event.target.checked,
                    value: !event.target.checked,
                    metadata: {
                      result_count: property.count,
                    },
                  })
                  property.onChange(event.target.checked)
                }}
                size="small"
                icon={<FilterCheckboxIcon />}
                checkedIcon={<FilterCheckboxCheckedIcon />}
              />
            }
            label={
              <Stack
                direction="row"
                spacing={1}
                sx={{
                  alignItems: "center",
                  justifyContent: "space-between",
                  width: "100%",
                }}
              >
                <Typography
                  sx={{
                    flexGrow: 1,
                    fontSize: "0.8125rem",
                  }}
                >
                  {t(property.key)}
                </Typography>
                <CountBadge value={property.count} />
              </Stack>
            }
            sx={{
              marginRight: 0,
              marginLeft: "-7px",
              "& .MuiFormControlLabel-label": {
                width: "100%",
              },
            }}
          />
        ))}
      </Stack>
    </FilterSection>
  )
}
